import React, { useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, MapPin, Plus, Loader2, Globe2, Users, ExternalLink, X, Ticket, Trash2, Pencil, Search, Share2, Bell, ImagePlus, MessageCircle, Map as MapIcon, Repeat, Flame, HandHeart } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COUNTRIES } from "@/lib/brand";
import { toast } from "sonner";
import ChatAvatar from "@/components/ChatAvatar";
import { CalendarButtons } from "@/pages/MyEvents";
import { shareEvent } from "@/lib/calendar";
import EventComments from "@/components/EventComments";
import EventMapModal from "@/components/EventMapModal";
import TopComments from "@/components/TopComments";
import { fmtDateTime } from "@/lib/date";

const REMINDER_OPTIONS = [
  { value: "week", label: "1 week before" },
  { value: "day", label: "1 day before" },
  { value: "hour", label: "1 hour before" },
  { value: "none", label: "No reminder" },
];

const TYPE_STYLES = {
  Service: "bg-[#5B7CFA]/15 text-[#8CA6FF]",
  Concert: "bg-[#E6B34A]/15 text-[#E6B34A]",
  Revival: "bg-[#FF3B30]/15 text-[#FF7A70]",
  Online: "bg-emerald-500/15 text-emerald-300",
  Conference: "bg-purple-500/15 text-purple-300",
  Outreach: "bg-teal-500/15 text-teal-300",
  Other: "bg-white/10 text-zinc-300",
};

function fmtDate(iso) {
  if (!iso) return "";
  return fmtDateTime(iso);
}

const emptyForm = { title: "", event_type: "Service", starts_at: "", country: "", city: "", venue: "", description: "", link: "", recurrence: "none" };

const RECURRENCE_OPTIONS = [
  { value: "none", label: "Does not repeat" },
  { value: "daily", label: "Every day" },
  { value: "weekly", label: "Every week" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Every month" },
];
const RECURRENCE_LABEL = { daily: "Daily", weekly: "Weekly", biweekly: "Biweekly", monthly: "Monthly" };

export default function Events() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [meta, setMeta] = useState({ event_types: [], countries: [] });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => user?.country || localStorage.getItem("eventsCountry") || "");
  const [needCountry, setNeedCountry] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [coverFile, setCoverFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const coverRef = useRef(null);
  const [commentsEvent, setCommentsEvent] = useState(null);
  const [mapEvent, setMapEvent] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [trendingIds, setTrendingIds] = useState(new Set());
  const galleryRef = useRef(null);
  const [galleryFiles, setGalleryFiles] = useState([]);

  useEffect(() => {
    if (user?.country && !localStorage.getItem("eventsCountry")) setSelected(user.country);
  }, [user]);

  useEffect(() => {
    if (!selected) { setNeedCountry(true); setLoading(false); return; }
    setNeedCountry(false);
    load(selected, query);
  }, [selected]);

  // debounce search
  useEffect(() => {
    if (!selected) return;
    const t = setTimeout(() => load(selected, query), 350);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => { api.get("/events/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!selected) return;
    const params = selected !== "All" ? { country: selected } : {};
    api.get("/events/trending", { params }).then((r) => setTrendingIds(new Set(r.data.map((e) => e.id)))).catch(() => {});
  }, [selected, events.length]);

  const load = (country, q) => {
    setLoading(true);
    const params = {};
    if (country && country !== "All") params.country = country;
    if (q && q.trim()) params.q = q.trim();
    api.get("/events", { params })
      .then((r) => { setEvents(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  const pickCountry = (c) => {
    setSelected(c);
    localStorage.setItem("eventsCountry", c);
  };

  const filterCountries = useMemo(() => {
    const set = new Set(meta.countries || []);
    if (selected && selected !== "All") set.add(selected);
    return ["All", ...Array.from(set).sort()];
  }, [meta.countries, selected]);

  const rsvp = async (ev, reminder) => {
    if (!user) { toast.error("Sign in to RSVP"); return; }
    try {
      const { data } = await api.post(`/events/${ev.id}/rsvp`, reminder ? { reminder } : {});
      setEvents((prev) => prev.map((e) => (e.id === ev.id ? { ...e, is_attending: data.attending, attendee_count: data.attendee_count, my_reminder: data.my_reminder } : e)));
      if (reminder) toast.success(reminder === "none" ? "Reminder turned off" : `We'll remind you ${REMINDER_OPTIONS.find((o) => o.value === reminder)?.label.toLowerCase()}`);
    } catch (e) { toast.error("Could not update RSVP"); }
  };

  const onShare = async (ev) => {
    const r = await shareEvent(ev);
    if (r === "copied") toast.success("Invite copied — share the good news!");
    else if (r === "error") toast.error("Could not share");
  };

  const del = async (ev) => {
    try { await api.delete(`/events/${ev.id}`); setEvents((prev) => prev.filter((e) => e.id !== ev.id)); toast.success("Event removed"); }
    catch (e) { toast.error("Could not delete"); }
  };

  const openForm = () => {
    if (!user) { toast.error("Sign in to add an event"); return; }
    setEditingId(null);
    setForm({ ...emptyForm, country: selected && selected !== "All" ? selected : (user.country || "") });
    setCoverFile(null);
    setGalleryFiles([]);
    setShowForm(true);
  };

  const openEdit = (ev) => {
    setEditingId(ev.id);
    setForm({
      title: ev.title || "", event_type: ev.event_type || "Service",
      starts_at: (ev.starts_at || "").slice(0, 16), country: ev.country || "",
      city: ev.city || "", venue: ev.venue || "", description: ev.description || "",
      link: ev.link || "", recurrence: ev.recurrence || "none",
    });
    setCoverFile(null);
    setGalleryFiles([]);
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.starts_at || !form.country) { toast.error("Title, date/time and country are required"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/events/${editingId}`, form);
        if (coverFile) {
          const fd = new FormData(); fd.append("file", coverFile);
          try { await api.post(`/events/${editingId}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } }); } catch (e) {}
        }
        for (const gf of galleryFiles.slice(0, 12)) {
          const fd = new FormData(); fd.append("file", gf);
          try { await api.post(`/events/${editingId}/gallery`, fd, { headers: { "Content-Type": "multipart/form-data" } }); } catch (e) {}
        }
        setSaving(false); setShowForm(false); setEditingId(null);
        toast.success("Event updated"); load(selected, query);
        return;
      }
      const { data } = await api.post("/events", form);
      if (coverFile) {
        const fd = new FormData();
        fd.append("file", coverFile);
        try { await api.post(`/events/${data.id}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } }); } catch (e) {}
      }
      for (const gf of galleryFiles.slice(0, 12)) {
        const fd = new FormData();
        fd.append("file", gf);
        try { await api.post(`/events/${data.id}/gallery`, fd, { headers: { "Content-Type": "multipart/form-data" } }); } catch (e) {}
      }
      setSaving(false);
      setShowForm(false);
      if (data.status === "approved") { toast.success("Event published 🎉"); load(selected, query); }
      else toast.success("Thanks! Your event was submitted for approval.");
    } catch (e) { setSaving(false); toast.error(e?.response?.data?.detail || "Could not submit event"); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-12 pt-8 pb-32" data-testid="events-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E6B34A]/10 text-[#E6B34A] mb-3">
            <CalendarDays className="w-4 h-4" /> <span className="overline">Community Events</span>
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none">Gospel Events</h1>
          <p className="text-zinc-400 mt-3 max-w-lg">Discover worship services, concerts and revivals near you — and share your own with the family worldwide.</p>
        </div>
        <button onClick={openForm} data-testid="add-event-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors">
          <Plus className="w-5 h-5" /> Add event
        </button>
      </div>

      {needCountry ? (
        <div className="max-w-md mx-auto text-center py-16 rounded-3xl border border-dashed border-white/10" data-testid="events-country-prompt">
          <Globe2 className="w-10 h-10 text-[#E6B34A] mx-auto mb-4" />
          <h3 className="font-display font-bold text-xl">Pick your country</h3>
          <p className="text-zinc-400 text-sm mt-2 mb-5">We'll show gospel events happening around you.</p>
          <select
            data-testid="events-country-select"
            className="w-full max-w-xs mx-auto px-4 py-3 rounded-xl bg-[#141C42] border border-white/10 text-white"
            defaultValue=""
            onChange={(e) => e.target.value && pickCountry(e.target.value)}
          >
            <option value="" disabled>Select a country…</option>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      ) : (
        <>
          <TopComments />
          <div className="flex items-center gap-3 mb-6 flex-wrap" data-testid="events-country-filter">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="events-search"
                placeholder="Search by title, city or church…"
                className="w-full pl-9 pr-3 py-2 rounded-xl bg-[#141C42] border border-white/10 text-sm text-white focus:border-[#E6B34A] focus:outline-none"
              />
            </div>
            <Globe2 className="w-4 h-4 text-[#E6B34A]" />
            <select
              value={filterCountries.includes(selected) ? selected : "All"}
              onChange={(e) => pickCountry(e.target.value)}
              data-testid="events-filter-select"
              className="px-4 py-2 rounded-xl bg-[#141C42] border border-white/10 text-sm text-white"
            >
              {filterCountries.map((c) => <option key={c} value={c}>{c === "All" ? "🌍 All countries" : c}</option>)}
            </select>
            <span className="text-sm text-zinc-500">{events.length} upcoming</span>
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#E6B34A]" /></div>
          ) : events.length === 0 ? (
            <div className="text-center py-16 rounded-2xl border border-dashed border-white/10" data-testid="events-empty">
              <p className="text-zinc-400">{query ? `No events match "${query}".` : `No events yet for ${selected === "All" ? "any country" : selected}.`}</p>
              {!query && <button onClick={openForm} className="mt-4 text-[#E6B34A] font-semibold hover:underline">Be the first to add one →</button>}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="events-grid">
              {events.map((ev) => (
                <div key={ev.id} data-testid={`event-card-${ev.id}`} className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden flex flex-col hover:border-white/20 transition-colors">
                  {ev.has_cover ? (
                    <div className="relative h-36 w-full overflow-hidden">
                      <img src={`${api.defaults.baseURL}/events/${ev.id}/cover`} alt={ev.title} className="w-full h-full object-cover" data-testid={`event-cover-${ev.id}`} />
                      <span className={`absolute top-3 left-3 text-[11px] font-bold px-2.5 py-1 rounded-full ${TYPE_STYLES[ev.event_type] || TYPE_STYLES.Other}`}>{ev.event_type}</span>
                    </div>
                  ) : null}
                  <div className="p-5 flex flex-col flex-1">
                  {!ev.has_cover && (
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${TYPE_STYLES[ev.event_type] || TYPE_STYLES.Other}`}>{ev.event_type}</span>
                    </div>
                  )}
                  {trendingIds.has(ev.id) && (
                    <span data-testid={`event-trending-${ev.id}`} className="self-start inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#FF3B30]/15 text-[#FF7A70] mb-2"><Flame className="w-3 h-3" fill="currentColor" /> Trending</span>
                  )}
                  <h3 className="font-display font-bold text-lg tracking-tight leading-tight">{ev.title}</h3>
                  {(user?.role === "admin" || user?.id === ev.created_by) && (
                    <div className="flex items-center gap-2 mt-2" data-testid={`event-manage-${ev.id}`}>
                      <button onClick={() => openEdit(ev)} data-testid={`event-edit-${ev.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 transition-colors"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                      <button onClick={() => del(ev)} data-testid={`event-del-${ev.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-white/5 text-zinc-400 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                    </div>
                  )}
                  <p className="text-sm text-[#E6B34A] mt-2 flex items-center gap-1.5 flex-wrap">
                    <CalendarDays className="w-4 h-4" /> {fmtDate(ev.starts_at)}
                    {ev.recurrence && ev.recurrence !== "none" && <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#5B7CFA]/20 text-[#8CA6FF]"><Repeat className="w-3 h-3" /> {RECURRENCE_LABEL[ev.recurrence]}</span>}
                  </p>
                  <button onClick={() => setMapEvent(ev)} data-testid={`event-map-${ev.id}`} className="text-sm text-zinc-400 mt-1.5 flex items-center gap-1.5 hover:text-[#E6B34A] transition-colors text-left">
                    <MapPin className="w-4 h-4 shrink-0" /> <span className="underline decoration-dotted">{[ev.venue, ev.city, ev.country].filter(Boolean).join(", ")}</span>
                  </button>
                  {ev.gallery && ev.gallery.length > 0 && (
                    <div className="flex gap-2 mt-3" data-testid={`event-gallery-${ev.id}`}>
                      {ev.gallery.slice(0, 4).map((g, i) => (
                        <button key={i} onClick={() => setLightbox({ items: ev.gallery, index: i, eventId: ev.id, canEdit: user?.role === "admin" || user?.id === ev.created_by })} className="relative w-14 h-14 rounded-lg overflow-hidden border border-white/10 hover:border-[#E6B34A] transition-colors">
                          <img src={`${api.defaults.baseURL}${g.url.replace('/api', '')}`} alt="" className="w-full h-full object-cover" />
                          {i === 3 && ev.gallery.length > 4 && <span className="absolute inset-0 bg-black/60 flex items-center justify-center text-xs font-bold text-white">+{ev.gallery.length - 4}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  {ev.description && <p className="text-sm text-zinc-400 mt-3 line-clamp-3">{ev.description}</p>}
                  <div className="flex items-center gap-2 mt-3 text-xs text-zinc-500">
                    <ChatAvatar userId={ev.created_by} name={ev.created_by_name} size={20} />
                    <span className="truncate">Shared by {ev.created_by_name || "a member"}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-auto pt-4">
                    <button
                      onClick={() => rsvp(ev)}
                      data-testid={`event-rsvp-${ev.id}`}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${ev.is_attending ? "bg-[#E6B34A] text-[#0A1030]" : "bg-white/5 hover:bg-white/10 text-zinc-200"}`}
                    >
                      <Ticket className="w-4 h-4" /> {ev.is_attending ? "Going" : "I'm interested"}
                    </button>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setCommentsEvent(ev)} data-testid={`event-comments-${ev.id}`} title="Comments" className="inline-flex items-center gap-1 p-2 rounded-full bg-white/5 hover:bg-white/10 text-zinc-300 transition-colors">
                        <MessageCircle className="w-4 h-4" /> {ev.comment_count > 0 && <span className="text-xs">{ev.comment_count}</span>}
                      </button>
                      <button onClick={() => onShare(ev)} data-testid={`event-share-${ev.id}`} title="Share event" className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-zinc-300 transition-colors">
                        <Share2 className="w-4 h-4" />
                      </button>
                      <span className="text-xs text-zinc-500 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {ev.attendee_count}</span>
                    </div>
                  </div>
                  {user && ev.is_attending && (
                    <div className="mt-3 flex items-center gap-2">
                      <Bell className="w-3.5 h-3.5 text-[#E6B34A] shrink-0" />
                      <select
                        value={ev.my_reminder || "day"}
                        onChange={(e) => rsvp(ev, e.target.value)}
                        data-testid={`event-reminder-${ev.id}`}
                        className="flex-1 px-2 py-1.5 rounded-lg bg-[#0C1338] border border-white/10 text-xs text-white focus:border-[#E6B34A] focus:outline-none"
                      >
                        {REMINDER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{`Remind me: ${o.label}`}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                    <CalendarButtons ev={ev} />
                    {ev.link && (
                      <a href={ev.link} target="_blank" rel="noreferrer" data-testid={`event-link-${ev.id}`} className="text-xs text-[#8CA6FF] hover:underline inline-flex items-center gap-1">
                        <ExternalLink className="w-3.5 h-3.5" /> More info
                      </a>
                    )}
                  </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="event-form-modal" onClick={() => setShowForm(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl border border-white/10 bg-[#141C42] p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display font-bold text-xl">{editingId ? "Edit event" : "Add an event"}</h3>
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <Field label="Event title *">
                <input data-testid="event-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} placeholder="Sunday Praise Night" />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Type">
                  <select data-testid="event-type" value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} className={inputCls}>
                    {(meta.event_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Date & time *">
                  <input data-testid="event-datetime" type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className={inputCls} />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Country *">
                  <select data-testid="event-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className={inputCls}>
                    <option value="">Select…</option>
                    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="City">
                  <input data-testid="event-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} placeholder="Atlanta" />
                </Field>
              </div>
              <Field label="Venue">
                <input data-testid="event-venue" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} className={inputCls} placeholder="Grace Cathedral" />
              </Field>
              <Field label="Link (livestream / registration)">
                <input data-testid="event-link" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} className={inputCls} placeholder="https://…" />
              </Field>
              <Field label="Description">
                <textarea data-testid="event-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className={inputCls} placeholder="Tell the community what to expect…" />
              </Field>
              <Field label="Cover image (optional)">
                <input ref={coverRef} type="file" accept="image/*" className="hidden" data-testid="event-cover-file" onChange={(e) => setCoverFile(e.target.files[0])} />
                <button type="button" onClick={() => coverRef.current?.click()} data-testid="event-cover-btn" className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#0C1338] border border-white/10 text-sm text-zinc-300 hover:border-[#E6B34A] transition-colors">
                  <ImagePlus className="w-4 h-4 text-[#E6B34A]" /> {coverFile ? coverFile.name : "Add a cover photo to make it pop"}
                </button>
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Repeats">
                  <select data-testid="event-recurrence" value={form.recurrence} onChange={(e) => setForm({ ...form, recurrence: e.target.value })} className={inputCls}>
                    {RECURRENCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>
                <Field label={`Photo gallery${galleryFiles.length ? ` (${galleryFiles.length})` : ""}`}>
                  <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" data-testid="event-gallery-file" onChange={(e) => setGalleryFiles(Array.from(e.target.files || []))} />
                  <button type="button" onClick={() => galleryRef.current?.click()} data-testid="event-gallery-btn" className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#0C1338] border border-white/10 text-sm text-zinc-300 hover:border-[#E6B34A] transition-colors">
                    <ImagePlus className="w-4 h-4 text-[#E6B34A]" /> {galleryFiles.length ? `${galleryFiles.length} photo(s)` : "Add photos"}
                  </button>
                </Field>
              </div>
            </div>
            <p className="text-[11px] text-zinc-500 mt-4">{editingId ? "Changes save immediately." : (user?.role === "admin" ? "As an admin, your event publishes immediately." : "Events are reviewed by our team before appearing publicly.")}</p>
            <button type="submit" disabled={saving} data-testid="event-submit" className="w-full mt-4 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : (editingId ? "Save changes" : "Submit event")}
            </button>
          </form>
        </div>
      )}
      {commentsEvent && (
        <EventComments
          ev={commentsEvent}
          onClose={() => setCommentsEvent(null)}
          onCountChange={(n) => setEvents((prev) => prev.map((e) => (e.id === commentsEvent.id ? { ...e, comment_count: n } : e)))}
        />
      )}
      {mapEvent && <EventMapModal ev={mapEvent} onClose={() => setMapEvent(null)} />}
      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85" data-testid="event-lightbox" onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)} className="absolute top-5 right-5 text-white/70 hover:text-white"><X className="w-7 h-7" /></button>
          <div className="max-w-3xl w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img src={`${api.defaults.baseURL}${lightbox.items[lightbox.index].url.replace('/api', '')}`} alt="" className="max-h-[75vh] max-w-full rounded-xl object-contain" />
            {lightbox.items[lightbox.index].caption && (
              <p className="mt-3 text-center text-sm text-zinc-200 bg-black/40 px-4 py-2 rounded-full" data-testid="lightbox-caption">{lightbox.items[lightbox.index].caption}</p>
            )}
            <button
              onClick={async () => {
                if (!user) { toast.error("Sign in to react"); return; }
                try {
                  const { data } = await api.post(`/events/${lightbox.eventId}/gallery/${lightbox.index}/amen`);
                  const items = lightbox.items.map((it, i) => (i === lightbox.index ? { ...it, i_amened: data.i_amened, amen_count: data.amen_count } : it));
                  setLightbox({ ...lightbox, items });
                  setEvents((prev) => prev.map((e) => (e.id === lightbox.eventId ? { ...e, gallery: items } : e)));
                } catch (e) { toast.error("Could not react"); }
              }}
              data-testid="lightbox-amen"
              className={`mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${lightbox.items[lightbox.index].i_amened ? "bg-[#E6B34A] text-[#0A1030]" : "bg-white/10 text-white hover:bg-white/20"}`}
            >
              <HandHeart className="w-4 h-4" /> Amen{lightbox.items[lightbox.index].amen_count > 0 ? ` · ${lightbox.items[lightbox.index].amen_count}` : ""}
            </button>
            {lightbox.canEdit && (
              <button
                onClick={async () => {
                  const cap = window.prompt("Photo caption", lightbox.items[lightbox.index].caption || "");
                  if (cap === null) return;
                  try {
                    const { data } = await api.put(`/events/${lightbox.eventId}/gallery/${lightbox.index}`, { caption: cap });
                    setLightbox({ ...lightbox, items: data.gallery });
                    setEvents((prev) => prev.map((e) => (e.id === lightbox.eventId ? { ...e, gallery: data.gallery } : e)));
                    toast.success("Caption saved");
                  } catch (e) { toast.error("Could not save caption"); }
                }}
                data-testid="lightbox-caption-edit"
                className="mt-2 text-xs text-[#E6B34A] hover:underline"
              >
                {lightbox.items[lightbox.index].caption ? "Edit caption" : "Add a caption"}
              </button>
            )}
          </div>
          {lightbox.items.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); setLightbox({ ...lightbox, index: (lightbox.index - 1 + lightbox.items.length) % lightbox.items.length }); }} className="absolute left-5 top-1/2 -translate-y-1/2 text-white/70 hover:text-white text-4xl px-2">‹</button>
              <button onClick={(e) => { e.stopPropagation(); setLightbox({ ...lightbox, index: (lightbox.index + 1) % lightbox.items.length }); }} className="absolute right-5 top-1/2 -translate-y-1/2 text-white/70 hover:text-white text-4xl px-2">›</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full px-4 py-2.5 rounded-xl bg-[#0C1338] border border-white/10 text-white text-sm focus:border-[#E6B34A] focus:outline-none";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline text-zinc-400 block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
